import React, { useState, useEffect } from 'react';
import { listAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser } from '../../services/adminApi';
import { notify, confirmAsync } from '../../utils/notify';
import './AdminUsers.css';

function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    first_name: '',
    last_name: '',
    role: 'admin',
    is_active: true
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await listAdminUsers();
      setAdmins(response.data);
    } catch (err) {
      console.error('Failed to load admin users:', err);
      notify.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await createAdminUser(formData);
      setShowCreateModal(false);
      resetForm();
      loadAdmins();
    } catch (err) {
      notify.error('Failed to create admin user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdate = async () => {
    try {
      await updateAdminUser(editingAdmin.id, formData);
      setEditingAdmin(null);
      resetForm();
      loadAdmins();
    } catch (err) {
      notify.error('Failed to update admin user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (adminId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to disable and demote this admin user?', danger: true }))) {
      return;
    }
    try {
      await deleteAdminUser(adminId);
      loadAdmins();
    } catch (err) {
      notify.error('Failed to delete admin user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      password: '',
      full_name: admin.full_name || '',
      first_name: admin.first_name || '',
      last_name: admin.last_name || '',
      role: admin.role,
      is_active: admin.is_active
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      first_name: '',
      last_name: '',
      role: 'admin',
      is_active: true
    });
    setEditingAdmin(null);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  if (loading) {
    return <div className="admin-users-loading">Loading admin users...</div>;
  }

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <h2>Admin User Management</h2>
        <button className="create-button" onClick={() => {
          resetForm();
          setShowCreateModal(true);
        }}>
          + Create Admin User
        </button>
      </div>

      <div className="admin-users-table-container">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td>{admin.id}</td>
                <td>{admin.email}</td>
                <td>{admin.full_name || '-'}</td>
                <td>
                  <span className={`role-badge ${admin.role}`}>
                    {admin.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${admin.is_active ? 'active' : 'inactive'}`}>
                    {admin.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(admin)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(admin.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="admin-modal">
          <div className="admin-modal-content">
            <h3>{editingAdmin ? 'Edit Admin User' : 'Create Admin User'}</h3>
            <div className="admin-form">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password {editingAdmin ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingAdmin}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="form-actions">
                <button
                  className="submit-button"
                  onClick={editingAdmin ? handleUpdate : handleCreate}
                >
                  {editingAdmin ? 'Update' : 'Create'}
                </button>
                <button
                  className="cancel-button"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;

